import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '../api/client'
import {
  INFLIGHT_STATES,
  inflightClear,
  inflightResend,
  inflightRows,
  type InflightRow,
  type InflightRows,
  type InflightScope,
  type InflightState,
} from '../api/inflight'
import { toast } from './notify'

/**
 * FEREASTRA „In flight" - `_partials/inflight_drawer.php` + `public/js/inflight_drawer.js`,
 * cu acelasi markup si aceeasi foaie (`public/css/inflight_drawer.css`).
 *
 * Se deschide din insignele din subsol (fiecare pe fila ei) sau din orice
 * element cu clasa `od-flow-open`. Randurile MELE se pot si lucra: Clear
 * scoate comanda din coada (sau sterge randul, daca s-a incheiat), Send again
 * o pune din nou la coada. Vederea pe firma e de citit (in afara de super
 * admin, `can_clear_any` - de la server, nu ghicit aici). Cand omul cere
 * firma si nu are voie, se spune pe fata (`refused`), nu se cade tacut pe
 * „ale mele".
 *
 * Dupa Clear/Resend se anunta `od:inflight-changed`, de care se agata
 * insignele din subsol ca sa se reincarce inainte de urmatorul pas de 30 s.
 */
export const OPEN_EVENT = 'od:inflight-open'
export const CHANGED_EVENT = 'od:inflight-changed'

export const TABS: Array<{ state: InflightState; label: string; icon: string; title: string }> = [
  { state: 'running', label: 'Running', icon: 'fas fa-spinner fa-spin', title: 'Running - the machine took it and is executing it now, no answer yet' },
  { state: 'waiting', label: 'Waiting', icon: 'far fa-clock', title: 'Waiting - queued; the machine has not asked for work yet, so nothing is running' },
  { state: 'failed', label: 'Failed', icon: 'fas fa-times-circle', title: 'Failed commands (last 24 hours)' },
  { state: 'succeeded', label: 'Succeeded', icon: 'fas fa-check-circle', title: 'Succeeded commands (last 24 hours)' },
  { state: 'changes', label: 'Changes', icon: 'fas fa-code-branch', title: 'Change requests waiting for a decision or still running' },
  { state: 'tickets', label: 'Tickets', icon: 'fas fa-ticket-alt', title: 'Tickets touched by this work (last 24 hours)' },
]

/* Aceleasi praguri ca `Inflight::STALE_*_MINUTES`. */
const STALE_WAITING = 60
const STALE_RUNNING = 15
const SHELL_TYPES = ['CON', 'CMD']
const CLEARABLE = ['waiting', 'running', 'failed', 'succeeded']

function minutes(value: string): number {
  const t = Date.parse(String(value || '').replace(' ', 'T'))
  return Number.isNaN(t) ? -1 : Math.max(0, Math.round((Date.now() - t) / 60000))
}

function ago(value: string): string {
  const t = Date.parse(String(value || '').replace(' ', 'T'))
  if (Number.isNaN(t)) return value
  const s = Math.max(0, Math.round((Date.now() - t) / 1000))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.round(s / 60)}m`
  if (s < 86400) return `${Math.round(s / 3600)}h`
  return `${Math.round(s / 86400)}d`
}

function stale(item: InflightRow): string {
  const m = minutes(item.at)
  if (m < 0) return ''
  if (item.state === 'waiting' && m >= STALE_WAITING) return 'nobody picked it up - the machine has not asked for work since it was queued'
  if (item.state === 'running' && m >= STALE_RUNNING) return 'no answer since it was picked up - it may not be running any more'
  return ''
}

function CommandLine({ item }: { item: InflightRow }) {
  const type = String(item.type || '').toUpperCase()
  const text = String(item.command || '')
  let channel = String(item.channel || '')
  if (!channel) {
    if (type === 'CON') channel = 'prompt'
    else if (SHELL_TYPES.includes(type)) channel = 'commands'
  }
  if (channel === 'ai') {
    return (
      <>
        <span className="od-flow-channel od-flow-channel-ai" title="AI agent">
          <i className="fas fa-robot" aria-hidden="true"></i>
        </span>{' '}
        {type && !SHELL_TYPES.includes(type) && <span className="od-flow-kind">{type} </span>}
        {text}
      </>
    )
  }
  if (channel === 'prompt') {
    return (
      <>
        <span className="od-flow-channel od-flow-channel-prompt" title="Command Prompt / Terminal">
          <i className="fas fa-window-restore" aria-hidden="true"></i>
        </span>{' '}
        {text}
      </>
    )
  }
  if (channel === 'commands' || SHELL_TYPES.includes(type)) {
    return (
      <>
        <span className="od-flow-channel od-flow-channel-commands" title={`${type || 'command'} - commands`}>
          <i className="fas fa-terminal" aria-hidden="true"></i>&gt;
        </span>{' '}
        {text}
      </>
    )
  }
  return (
    <>
      <span className="od-flow-kind">{type}</span> {text}
    </>
  )
}

export function openInflight(state?: InflightState): void {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { state } }))
}

export function InflightDrawer() {
  const [open, setOpen] = useState(false)
  const [state, setState] = useState<InflightState>('running')
  const [scope, setScope] = useState<InflightScope>('mine')
  const [data, setData] = useState<InflightRows | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [canClearAny, setCanClearAny] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async (wantScope: InflightScope, wantState: InflightState): Promise<void> => {
    setLoading(true)
    setFailed(false)
    try {
      const reply = await inflightRows(wantScope, wantState)
      setData(reply)
      /* Serverul spune pe ce vedere a raspuns: pe refuz se ramane pe „mine". */
      setScope(reply.scope)
      if (reply.refused) toast('Company view is off for your role. Ask an administrator to turn it on in Settings.', 'In flight', 'warning')
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const onOpen = (ev: Event): void => {
      const wanted = (ev as CustomEvent<{ state?: InflightState }>).detail?.state
      const next = wanted && (INFLIGHT_STATES as readonly string[]).includes(wanted) ? wanted : state
      setState(next)
      setOpen(true)
      void load(scope, next)
    }
    const onClick = (ev: MouseEvent): void => {
      const target = (ev.target as HTMLElement | null)?.closest('.od-flow-open')
      if (target === null || target === undefined) return
      ev.preventDefault()
      openInflight((target as HTMLElement).dataset.state as InflightState | undefined)
    }
    window.addEventListener(OPEN_EVENT, onOpen)
    document.addEventListener('click', onClick)
    return () => {
      window.removeEventListener(OPEN_EVENT, onOpen)
      document.removeEventListener('click', onClick)
    }
  }, [load, scope, state])

  useEffect(() => {
    const badges = document.getElementById('od-inflight')
    setCanClearAny(badges?.dataset.canClearAny === '1')
  }, [open, data])

  useEffect(() => {
    if (!open) return
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const pickTab = (next: InflightState): void => {
    setState(next)
    void load(scope, next)
  }

  const pickScope = (next: InflightScope): void => {
    setScope(next)
    void load(next, state)
  }

  const act = async (item: InflightRow, what: 'clear' | 'resend'): Promise<void> => {
    const key = `${what}:${item.origin}:${item.id}`
    setBusy(key)
    try {
      const reply = what === 'clear' ? await inflightClear(item.origin, item.id) : await inflightResend(item.origin, item.id)
      const ok = (reply as { ok?: boolean }).ok !== false && (reply as { status?: string }).status !== 'ERROR'
      if (!ok) {
        toast((reply as { error?: string; message?: string }).error ?? (reply as { message?: string }).message ?? 'That did not work.', 'In flight', 'error')
      } else {
        await load(scope, state)
        window.dispatchEvent(new CustomEvent(CHANGED_EVENT))
      }
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'That did not work.', 'In flight', 'error')
    } finally {
      setBusy(null)
    }
  }

  const tally = (key: InflightState): string => {
    const mine = data?.counts.mine
    const all = data?.counts.company
    const n = (c?: Record<string, number>): number => (c ? Number(c[key] ?? 0) : 0)
    return scope === 'company' && all ? `${n(mine)}/${n(all)}` : String(n(mine))
  }

  const actions = (item: InflightRow) => {
    if ((scope !== 'mine' && !canClearAny) || !item.origin || !item.id) return null
    const running = item.state === 'running'
    const mute = running && minutes(item.at) >= STALE_RUNNING
    const clearable = CLEARABLE.includes(item.state)
    if (!clearable && running && !mute) return null
    return (
      <div className="od-flow-acts">
        {clearable && (
          <button
            type="button"
            className="od-flow-act"
            data-act="clear"
            disabled={busy !== null}
            title={item.state === 'failed' || item.state === 'succeeded' ? 'takes this row off the list for good' : 'takes it out of the queue so the machine never runs it'}
            onClick={() => void act(item, 'clear')}
          >
            {busy === `clear:${item.origin}:${item.id}` ? '...' : 'Clear'}
          </button>
        )}
        {(!running || mute) && (
          <button type="button" className="od-flow-act" data-act="resend" disabled={busy !== null} onClick={() => void act(item, 'resend')}>
            {busy === `resend:${item.origin}:${item.id}` ? '...' : 'Send again'}
          </button>
        )}
      </div>
    )
  }

  const rows = data?.rows ?? []

  return (
    <>
      <div id="odFlowOverlay" className={`od-flow-overlay${open ? ' is-open' : ''}`} aria-hidden={!open} onClick={() => setOpen(false)}></div>
      <aside id="odFlowDrawer" className={`od-flow-drawer${open ? ' is-open' : ''}`} aria-hidden={!open}>
        <div className="od-flow-head">
          <strong>In flight</strong>
          <div className="od-flow-scope" id="odFlowScope" hidden={!data?.may_company}>
            {(['mine', 'company'] as const).map((s) => (
              <button key={s} type="button" className={`od-flow-scope-pick${scope === s ? ' is-on' : ''}`} data-scope={s} onClick={() => pickScope(s)}>
                {s === 'mine' ? 'Mine' : 'Company'}
              </button>
            ))}
          </div>
          <button type="button" className="od-flow-refresh" id="odFlowRefresh" title="Reload" disabled={loading} onClick={() => void load(scope, state)}>
            &#8635;
          </button>
          <button type="button" className="od-flow-close" aria-label="Close" onClick={() => setOpen(false)}>
            &times;
          </button>
        </div>
        <div className="od-flow-tabs" id="odFlowTabs">
          {TABS.map((tab) => (
            <button
              key={tab.state}
              type="button"
              className={`od-flow-tab od-flow-tab-${tab.state}${state === tab.state ? ' is-on' : ''}`}
              data-state={tab.state}
              data-icon={tab.icon}
              title={tab.title}
              onClick={() => pickTab(tab.state)}
            >
              <i className={tab.icon} aria-hidden="true"></i> {tab.label} <b className="od-flow-n">{tally(tab.state)}</b>
            </button>
          ))}
        </div>
        <div className="od-flow-body" id="odFlowBody">
          {failed ? (
            <div className="od-flow-muted">Could not load. Try again.</div>
          ) : loading && data === null ? (
            <div className="od-flow-muted">...</div>
          ) : rows.length === 0 ? (
            <div className="od-flow-muted">Nothing in flight here.</div>
          ) : (
            rows.map((item) => {
              const why = stale(item)
              const who = scope === 'company' ? item.user_name || '' : ''
              const msg = item.message || ''
              const pill = item.state && item.state !== state ? TABS.find((t) => t.state === item.state) : undefined
              return (
                <div className={`od-flow-item${why ? ' is-stale' : ''}`} data-id={item.id} data-origin={item.origin} key={`${item.origin}:${item.id}`}>
                  <div className="od-flow-line">
                    {item.state && item.state !== state && (
                      <span className={`od-flow-state od-flow-state-${item.state}`}>
                        {pill && <i className={pill.icon} aria-hidden="true"></i>} {item.state}
                      </span>
                    )}
                    <span className="od-flow-dev">{item.device_name || `device #${item.device_id || 0}`}</span>
                    {who && <span className="od-flow-who">{who}</span>}
                    <span className="od-flow-when">{ago(item.at)}</span>
                  </div>
                  <div className="od-flow-cmd">
                    <CommandLine item={item} />
                  </div>
                  {why && <div className="od-flow-stale">{why}</div>}
                  {msg && <div className="od-flow-msg">{msg.length > 240 ? `${msg.slice(0, 240)}…` : msg}</div>}
                  {actions(item)}
                </div>
              )
            })
          )}
        </div>
        <div className="od-flow-foot">
          <span className="od-flow-muted" id="odFlowNote">
            {scope === 'mine' || canClearAny ? 'your work; Clear takes it out of the queue' : 'read only'}
          </span>
        </div>
      </aside>
    </>
  )
}
